import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from './client';
import type { TaskDto, TaskDependencyDto, TaskLinksDto, PagedResult, PreviewAssignResult, BulkReassignResult, BulkCreateTasksResult } from '../types/api';

export interface TaskListParams {
  projectId?: string;
  assigneeId?: string;
  status?: string;
  taskType?: string;
  sprintId?: string;
  epicId?: string;
  limit?: number;
  cursor?: string;
}

export interface CreateTaskRequest {
  title: string;
  description?: string;
  points: number;
  dueDate: string;
  projectId: string;
  assigneeId?: string;
  type?: string;
  epicId?: string;
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  acceptanceCriteria?: string;
  severity?: string;
  epicId?: string;
  removeFromEpic?: boolean;
  points?: number;
  dueDate?: string;
  assigneeId?: string;
  type?: string;
  status?: string;
  sprintId?: string;
  removeFromSprint?: boolean;
  markDone?: boolean;
}

export const taskKeys = {
  all:          ()                        => ['tasks']                       as const,
  list:         (p: TaskListParams)       => ['tasks', 'list', p]            as const,
  detail:       (id: string)              => ['tasks', id]                   as const,
  dependencies: (id: string)              => ['tasks', id, 'dependencies']   as const,
};

export function useTaskList(params: TaskListParams) {
  return useQuery({
    queryKey: taskKeys.list(params),
    queryFn: () => client.get<PagedResult<TaskDto>>('/tasks', { params }).then((r) => r.data),
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => client.get<TaskDto>(`/tasks/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskRequest) => client.post<TaskDto>('/tasks', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all() }),
  });
}

export function useUpdateTask(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateTaskRequest) => client.patch<TaskDto>(`/tasks/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(id) });
      qc.invalidateQueries({ queryKey: taskKeys.all() });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all() }),
  });
}

export function useFlagBlocker(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => client.post<TaskDto>(`/tasks/${taskId}/blocker`, { reason }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.all() });
    },
  });
}

export function useClearBlocker(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.delete<TaskDto>(`/tasks/${taskId}/blocker`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.all() });
    },
  });
}

export function usePreviewAssign(taskId: string) {
  return useMutation({
    mutationFn: (assigneeId: string) =>
      client.post<PreviewAssignResult>(`/tasks/${taskId}/preview-assign`, { assigneeId }).then((r) => r.data),
  });
}

export function useBulkReassign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { taskIds: string[]; targetEngineerId: string }) =>
      client.post<BulkReassignResult>('/tasks/bulk-reassign', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all() }),
  });
}

export interface BulkCreateTaskItem {
  title: string;
  description?: string;
  points: number;
  dueDate: string;
  assigneeId?: string;
  type?: string;
}

export function useBulkCreateTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { projectId: string; tasks: BulkCreateTaskItem[] }) =>
      client.post<BulkCreateTasksResult>('/tasks/bulk', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskKeys.all() }),
  });
}

export function useTaskDependencies(taskId: string) {
  return useQuery({
    queryKey: taskKeys.dependencies(taskId),
    queryFn: () => client.get<TaskLinksDto>(`/tasks/${taskId}/dependencies`).then((r) => r.data),
    enabled: !!taskId,
  });
}

export function useAddDependency(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockingTaskId: string) =>
      client.post<TaskDependencyDto>(`/tasks/${taskId}/dependencies`, { blockingTaskId }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.dependencies(taskId) });
    },
  });
}

export function useRemoveDependency(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockingTaskId: string) =>
      client.delete(`/tasks/${taskId}/dependencies/${blockingTaskId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
      qc.invalidateQueries({ queryKey: taskKeys.dependencies(taskId) });
    },
  });
}

export function useLoanTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { targetEngineerId: string; reason?: string }) =>
      client.post<TaskDto>(`/tasks/${taskId}/loan`, body).then((r) => r.data),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: taskKeys.detail(task.id) });
      qc.invalidateQueries({ queryKey: taskKeys.all() });
    },
  });
}
